import React, {useEffect, useState} from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '../components/UI/Button'
import { Input } from '../components/UI/Input'
import { Pod24CommonLayout } from '../layouts/Pod24CommonLayout'
import { userApiService } from '../../infrastructure/api/user-api-service'
import {useUserStore} from "../stores/user-store";
import {useAuthStore} from "../stores/auth-store";

// 소셜 회원가입 스키마
const SocialSignupSchema = z.object({
  name: z.string().min(1, '이름을 입력해주세요'),
  agreementTerms: z.boolean().refine(val => val === true, {
    message: "서비스 이용약관에 동의해주세요"
  }),
  agreementPrivacy: z.boolean().refine(val => val === true, {
    message: "개인정보 수집및 이용에 동의해주세요"
  }),
  agreementAge: z.boolean().refine(val => val === true, {
    message: "만 14세 이상입니다에 동의해주세요"
  }),
  agreementMarketing: z.boolean().optional()
})

type SocialSignupFormData = {
  name: string
  agreementTerms: boolean
  agreementPrivacy: boolean
  agreementAge: boolean
  agreementMarketing?: boolean
}

export const SocialSignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectAll, setSelectAll] = useState(false);

  const {fetchProfile, profile, updateProfile} = useUserStore();
  const {user} = useAuthStore();

  const [userEmail, setUserEmail] = useState("");

  const [provider, setProvider] = useState<null | "naver" | "kakao" | "google">(null);

  useEffect(() => {
    (async () => {
      if (user?.id != null) {
        await fetchProfile(user?.id);
      }
   })();
  }, [fetchProfile, navigate, user?.id])

  useEffect(() => {
    if (!profile) return;

    if (profile?.name) {
      navigate("/signup/complete");
    }
  }, [navigate, profile]);

  // 페이지 접속 시 유저가 등록한 이름이 존재할 경우 홈으로 이동함.
  useEffect(() => {
    if (!profile) return;

    if (!profile.email || !profile.socialAuth) {
      throw new Error("유저 정보를 정상적으로 가져올 수 없음.");
    }

    setUserEmail(profile.email);

    switch (profile.socialAuth[0]) {
      case "NAVER":
        setProvider("naver")
        break;
      case "KAKAO":
        setProvider("kakao")
        break;
      case "GOOGLE":
        setProvider("google")
        break;
    }

  }, [navigate, profile]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<SocialSignupFormData>({
    resolver: zodResolver(SocialSignupSchema),
    defaultValues: {
      agreementTerms: false,
      agreementPrivacy: false,
      agreementAge: false,
      agreementMarketing: false
    }
  })

  // 폼 값 감시
  const agreementValues = watch(['agreementTerms', 'agreementPrivacy', 'agreementAge', 'agreementMarketing'])
  
  // 전체 선택 핸들러
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked)
    setValue('agreementTerms', checked)
    setValue('agreementPrivacy', checked)  
    setValue('agreementAge', checked)
    setValue('agreementMarketing', checked)
  }

  // 개별 체크박스 상태에 따른 전체 선택 상태 업데이트
  React.useEffect(() => {
    const [terms, privacy, age, marketing] = agreementValues
    const allRequired = terms && privacy && age
    const allChecked = allRequired && (marketing ?? false)
    setSelectAll(allChecked)
  }, [agreementValues])

  const onSubmit = async (data: SocialSignupFormData) => {
    console.log('submitting data', data);

    if (user?.id) {
      try {
        // 서버에 프로필 업데이트
        await updateProfile({name: data.name});

        const currentProfile = useUserStore.getState().profile;
        if (currentProfile) {
          useAuthStore.setState((state) => ({
            ...state,
            user: currentProfile
          }));
        }
      } catch (error) {
        console.error('프로필 업데이트 실패:', error);
        // 에러 처리 로직 추가 가능
      }
    }
  }

  return (
    <Pod24CommonLayout>
      <div className="flex justify-center px-4" style={{paddingTop: '100px'}}>
        <div className="w-full" style={{maxWidth: '560px'}}>
          {/* 제목 */}
          <div className="text-left" style={{marginBottom: '36px', width: '560px', height: '68px'}}>
            <h1 className="font-['Pretendard'] font-normal text-gray-900" style={{fontSize: '24px'}}>
              회원가입 하고
            </h1>
            <h2 className="font-['Pretendard'] font-semibold text-gray-900" style={{fontSize: '24px'}}>
              서비스를 이용하세요.
            </h2>
          </div>

          {/* 구분선 */}
          <div className="w-full border-t border-gray-300" style={{marginBottom: '16px'}}></div>

          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* 이름 또는 업체명 */}
            <div>
              <label className="block text-left font-['Pretendard'] font-medium text-gray-900" style={{fontSize: '16px', marginBottom: '12px'}}>
                이름 또는 업체명
              </label>
              <Input
                type="text"
                placeholder="이름 또는 업체명을 입력해 주세요."
                autoComplete="name"
                {...register('name')}
                error={errors.name?.message}
                className="w-full"
                style={{width: '560px', height: '40px', borderRadius: '8px'}}
              />
            </div>

            {/* 소셜 계정 정보 표시 */}
            <div>
              <label className="block text-left font-['Pretendard'] font-medium text-gray-900" style={{fontSize: '16px', marginBottom: '12px'}}>
                아래 계정으로 가입됩니다
              </label>
              <div className="relative">
                <Input
                  type="text"
                  value={userEmail}
                  readOnly
                  disabled
                  className="w-full pl-12"
                  style={{width: '560px', height: '40px', borderRadius: '8px'}}
                />
                {provider === 'naver' && (
                  <img
                    src="/assets/icons/naver-icon.svg"
                    alt="naver"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-6 h-6"
                  />
                )}
                {provider === 'kakao' && (
                  <img
                    src="/assets/icons/kakao-icon.svg"
                    alt="kakao"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-6 h-6"
                  />
                )}
                {provider === 'google' && (
                  <img
                    src="/assets/icons/google-icon.svg"
                    alt="google"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-6 h-6"
                  />
                )}
              </div>
            </div>

            {/* 약관 동의 */}
            <div className="space-y-4">
              {/* 전체 선택 체크박스 */}
              <label className="flex items-center cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="sr-only"
                  />
                  <img 
                    src={selectAll ? "/assets/icons/checkbox-all-checked.svg" : "/assets/icons/checkbox-all-unchecked.svg"}
                    alt="checkbox"
                    className="w-6 h-6"
                  />
                </div>
                <span className="ml-2 font-['Pretendard'] font-medium text-gray-700" style={{fontSize: '14px'}}>
                  전체 선택
                </span>
              </label>
              
              <div className="space-y-3 ml-6">
                {/* 필수 약관들 */}
                <label className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      {...register('agreementTerms')}
                      className="sr-only"
                    />
                    <img 
                      src={watch('agreementTerms') ? "/assets/icons/checkbox-checked.svg" : "/assets/icons/checkbox-unchecked.svg"}
                      alt="checkbox"
                      className="w-6 h-6"
                    />
                  </div>
                  <span className="ml-2 font-['Pretendard'] font-medium text-gray-700" style={{fontSize: '14px'}}>
                    (필수) 서비스 이용약관 동의
                  </span>
                </label>
                {errors.agreementTerms && (
                  <p className="text-red-600 text-sm ml-6">{errors.agreementTerms.message}</p>
                )}

                <label className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      {...register('agreementPrivacy')}
                      className="sr-only"
                    />
                    <img 
                      src={watch('agreementPrivacy') ? "/assets/icons/checkbox-checked.svg" : "/assets/icons/checkbox-unchecked.svg"}
                      alt="checkbox"
                      className="w-6 h-6"
                    />
                  </div>
                  <span className="ml-2 font-['Pretendard'] font-medium text-gray-700" style={{fontSize: '14px'}}>
                    (필수) 개인정보 수집 및 이용 동의
                  </span>
                </label>
                {errors.agreementPrivacy && (
                  <p className="text-red-600 text-sm ml-6">{errors.agreementPrivacy.message}</p>
                )}

                <label className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      {...register('agreementAge')}
                      className="sr-only"
                    />
                    <img 
                      src={watch('agreementAge') ? "/assets/icons/checkbox-checked.svg" : "/assets/icons/checkbox-unchecked.svg"}
                      alt="checkbox"
                      className="w-6 h-6"
                    />
                  </div>
                  <span className="ml-2 font-['Pretendard'] font-medium text-gray-700" style={{fontSize: '14px'}}>
                    (필수) 만 14세 이상입니다
                  </span>
                </label>
                {errors.agreementAge && (
                  <p className="text-red-600 text-sm ml-6">{errors.agreementAge.message}</p>
                )}

                {/* 선택 약관 */}
                <label className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      {...register('agreementMarketing')}
                      className="sr-only"
                    />
                    <img 
                      src={watch('agreementMarketing') ? "/assets/icons/checkbox-checked.svg" : "/assets/icons/checkbox-unchecked.svg"}
                      alt="checkbox"
                      className="w-6 h-6"
                    />
                  </div>
                  <span className="ml-2 font-['Pretendard'] font-medium text-gray-700" style={{fontSize: '14px'}}>
                    (선택) 이벤트, 혜택, 신제품 소식 받기
                  </span>
                </label>
              </div>
            </div>

            {/* 회원가입 버튼 */}
            <Button
              type="submit"
              className="w-full bg-[#4285F4] hover:bg-[#3367D6] text-white font-['Pretendard'] font-semibold transition-colors"
              style={{fontSize: '16px', width: '560px', height: '48px', borderRadius: '8px', marginBottom: '118px'}}
            >
              회원 가입하기
            </Button>
          </form>
        </div>
      </div>
    </Pod24CommonLayout>
  )
}